import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Send,
  MessageCircle,
  Hash,
  MessageSquare,
  Users,
  MessagesSquare,
  ChevronDown,
  Construction,
  Menu,
  X,
  Server,
} from "lucide-react";
import { useAdminConnectionCounts } from "@/hooks/useAdminConnectionCounts";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import TelegramConnector from "@/components/admin/TelegramConnector";
import WhatsAppConnector from "@/components/admin/WhatsAppConnector";
import OutboundMcpServers from "@/components/admin/OutboundMcpServers";

const messengerItems = [
  { id: "telegram", label: "Telegram", icon: Send },
  { id: "whatsapp", label: "WhatsApp", icon: MessageCircle },
  { id: "slack", label: "Slack", icon: Hash },
  { id: "google-chat", label: "Google Chat", icon: MessageSquare },
  { id: "ms-teams", label: "Microsoft Teams", icon: Users },
];

const mcpItems = [{ id: "mcp-servers", label: "MCP Servers", icon: Server }];

const allItems = [...messengerItems, ...mcpItems];

const ExternalInterface = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const counts = useAdminConnectionCounts();
  const [active, setActive] = useState(() => {
    const fromUrl = searchParams.get("connector");
    const valid = allItems.find((i) => i.id === fromUrl);
    return valid ? fromUrl! : "telegram";
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const selectConnector = (id: string) => {
    setActive(id);
    setSidebarOpen(false);
    setSearchParams((prev) => {
      prev.set("connector", id);
      return prev;
    });
  };

  const isMessengerActive = messengerItems.some((i) => i.id === active);
  const isMcpActive = mcpItems.some((i) => i.id === active);

  const [msgOpen, setMsgOpen] = useState(true);
  const [mcpOpen, setMcpOpen] = useState(true);
  const activeItem = allItems.find((i) => i.id === active);

  return (
    <div className="flex flex-1 min-h-0 relative overflow-hidden">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-30 bg-black/30 md:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <div
        className={cn(
          "fixed inset-y-0 left-0 z-40 transition-transform duration-300",
          "md:relative md:inset-auto md:z-auto md:translate-x-0 md:flex md:shrink-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <aside className="flex h-full w-56 flex-col border-r border-border bg-sidebar">
          <div className="flex items-center justify-between px-4 py-3 shrink-0">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">External Interface</h2>
            <Button variant="ghost" size="icon" className="h-6 w-6 md:hidden" onClick={() => setSidebarOpen(false)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          <nav className="flex-1 space-y-1 px-2 pb-4 overflow-y-auto">
            <Collapsible open={msgOpen} onOpenChange={setMsgOpen}>
              <CollapsibleTrigger asChild>
                <button
                  className={cn(
                    "flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                    isMessengerActive
                      ? "text-sidebar-accent-foreground"
                      : "text-sidebar-foreground hover:bg-sidebar-accent/50",
                  )}
                >
                  <MessagesSquare className="h-4 w-4" />
                  Messenger
                  <ChevronDown className={cn("ml-auto h-3 w-3 transition-transform", msgOpen && "rotate-180")} />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="ml-3 space-y-0.5 border-l border-border pl-2">
                  {messengerItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => selectConnector(item.id)}
                      className={cn(
                        "flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors",
                        active === item.id
                          ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                          : "text-sidebar-foreground hover:bg-sidebar-accent/50",
                      )}
                    >
                      <item.icon className="h-3.5 w-3.5" />
                      <span className="flex-1 text-left">{item.label}</span>
                      {(counts[item.id] ?? 0) > 0 && (
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                          {counts[item.id]}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </nav>
        </aside>
      </div>

      {/* Main content */}
      <div className="flex flex-1 flex-col min-h-0 overflow-hidden">
        {/* Mobile toolbar */}
        <div className="flex items-center gap-2 border-b border-border px-3 py-2 md:hidden shrink-0">
          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => setSidebarOpen(true)}>
            <Menu className="h-5 w-5" />
          </Button>
          <span className="text-sm font-medium">{activeItem?.label}</span>
        </div>

        {active === "telegram" ? (
          <TelegramConnector />
        ) : active === "whatsapp" ? (
          <WhatsAppConnector />
        ) : active === "mcp-servers" ? (
          <OutboundMcpServers />
        ) : (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 px-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-accent">
              <Construction className="h-8 w-8 text-accent-foreground" />
            </div>
            <h1 className="text-2xl font-semibold">{activeItem?.label}</h1>
            <p className="max-w-md text-center text-muted-foreground">
              This feature is coming soon. Check back later for updates.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExternalInterface;
